import { OptionItemState, TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useKeyOperation } from 'components/WorkspaceEditor/features/keyOperations/hooks/useKeyOperation'
import { useCKEditorToggle } from 'components/WorkspaceEditor/hooks'
import { useContentEditorFocus } from 'components/WorkspaceEditor/hooks/useContentEditorFocus'
import { useTransactionManager } from 'components/WorkspaceEditor/services'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useNodeMapShallowSelector } from '../../features/keyOperations/store/NodeMapStore'
import { PROPERTY_EDITOR_PILL_STATE_VARIANT } from '../../features/pillSelection/constants'
import { useShallowSelector, useWorkspace2Store } from '../../Workspace2Store'
import {
    ClassicEditorHandler,
    ClassicEditorOnReadyHandler,
} from '../ContentEditor'
import {
    extractSuggestions,
    getUnresolvedChanges,
} from '../ContentEditor/contentUtils'
import { useFormatReplacementOption } from './hooks/useFormatReplacementOption'
import { UnitsOfMeasure, UnitsOfMeasureKey } from 'models/projectSettings'
import { useProjectSettings } from '../../Workspace2Store'
import { hasUOMContent, hasUOMContentMatchesSetting } from 'components/WorkspaceEditor/utils/uomUtils'
import { normalizeSpaces } from 'shared/utils/stringUtils'

/** Event handlers for ReplacementOption CKEditor interactions */
interface ReplacementOptionHandler {
    /**
     * Handler called when CKEditor is ready and initialized.
     * Sets the editor reference for transaction management and rollback capabilities.
     */
    onReady: ClassicEditorOnReadyHandler

    /**
     * Handler called when CKEditor loses focus.
     * - Deactivates the editor when there are no content changes
     * - Processes and saves changes when content has been modified
     * - Triggers optimistic mutations for immediate UI updates
     */
    onBlur: ClassicEditorHandler

    /**
     * Handler for keyboard events while CKEditor has focus.
     * Handles special key combinations:
     * - Enter: Save current content and add new content
     * - Ctrl+S: Save content only
     * - Escape: Deactivate editor and revert changes
     */
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void

    /**
     * Handler called when CKEditor content changes.
     */
    onChange: ClassicEditorHandler

    /**
     * Handler called when CKEditor receives focus.
     * Activates the editor to enable content editing.
     */
    onFocusCapture: ClassicEditorHandler

    /**
     * Handler called after ContentEditor's configureEditor() completes.
     * Sets editorMountedRef to allow blur processing and calls markMountComplete
     * to allow deactivation.
     */
    onConfigComplete: () => void
}

interface ReplacementOptionControllerOptions {
    /** When true, clearing the field to empty is a valid change (e.g., drawing designation). */
    allowEmpty?: boolean
}

interface UseFormatReplacementOptionReturn {
    handlers: ReplacementOptionHandler
    ckeToggle: ReturnType<typeof useCKEditorToggle<string>>
    /** Sets the original optionItemState for revert on blur without changes */
    setOriginalState: (state: OptionItemState | undefined) => void
}

/**
 * Calculates the baseline text that is actually loaded into CKEditor.
 * This must match the editorText logic used in ReplacementOption.tsx during
 * CKEditor initialization to ensure change detection compares against the
 * actual loaded content (not raw contentItem.text with UOM delimiters/whitespace).
 *
 * @param formattedText - The formatted text with UOM delimiters removed
 * @param rawText - The raw contentItem.text value
 * @returns The baseline text that will be loaded into the editor
 */
const getEditorBaselineText = (formattedText: string | undefined, rawText: string | undefined): string => {
    return formattedText || rawText?.trim() || ''
}

interface ChangeDetectionParams {
    /** Resolved content from getUnresolvedChanges */
    currentContent: string
    /** Source content item — text and placeholderText are derived internally */
    contentItem: Partial<TextContentElement>
    /** True when editing an OptedIn item (has original state to revert to) */
    isOptedInEdit: boolean
    /** Allows clearing to empty as a valid change (e.g., drawing designation) */
    allowEmpty?: boolean
    /** Raw CKEditor HTML — disambiguates "no changes" vs "user cleared to empty" */
    editorData?: string
}

/**
 * Returns true if content has NOT changed from its original value.
 *
 * - allowEmpty + editorData: checks raw HTML to distinguish "no changes" from "cleared to empty"
 * - OptedIn edits: unchanged if empty OR matches original (after space normalization)
 * - Regular edits: unchanged if empty OR matches both original AND placeholder
 */
const hasContentNotChanged = ({
    currentContent,
    contentItem,
    isOptedInEdit,
    allowEmpty = false,
    editorData = '',
}: ChangeDetectionParams): boolean => {
    const originalText = contentItem?.text ?? ''
    const placeholderText = contentItem?.placeholderText ?? ''

    // getUnresolvedChanges returns '' for both "no changes" and "cleared to empty".
    // Check raw editor HTML to tell them apart (Bug 2589647 / Bug 2583788).
    if (allowEmpty && currentContent === '' && originalText !== '' && originalText !== placeholderText) {
        const isEditorActuallyEmpty = editorData
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim() === ''
        if (isEditorActuallyEmpty) {
            return false // User deliberately cleared the field
        }
    }

    const normalizedCurrent = normalizeSpaces(currentContent)
    const normalizedOriginal = normalizeSpaces(originalText)

    if (isOptedInEdit) {
        return currentContent === '' || normalizedCurrent === normalizedOriginal
    } else {
        return currentContent === '' || (normalizedCurrent === normalizedOriginal && originalText === placeholderText)
    }
}

/**
 * A hook that provides controller functionality for replacement options in the editor.
 * It handles focus, blur, and change events for a replacement option component.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item representing the replacement option
 * @param {Function} [onValueChange] - Optional callback that's called when the content item's value changes
 * @param {ReplacementOptionControllerOptions} [options] - Additional controller options
 * @returns {Object} An object containing event handlers for the replacement option component
 */
export const useReplacementOptionController = (
    contentItem: Partial<TextContentElement>,
    onValueChange?: (
        currentSelectedItem: Partial<TextContentElement>,
        currentlySelected: boolean
    ) => void,
    options?: ReplacementOptionControllerOptions
): UseFormatReplacementOptionReturn => {
    const { allowEmpty = false } = options ?? {}
    const { registerEditorRef, editorRef } = useTransactionManager()
    const {
        formattedText,
        getUpdatedContentItem,
        removeUnitOfMeasureDelimiters,
    } = useFormatReplacementOption(contentItem)

    // The baseline text that is actually loaded into CKEditor.
    // Used to prevent false positives when Firefox fires onChange during initialization.
    const editorBaselineText = useMemo(
        () => getEditorBaselineText(formattedText, contentItem.text),
        [formattedText, contentItem.text]
    )
    // State to track unsaved content changes in the editor
    const [unsavedData, setUnsavedData] = useState<string>(
        contentItem.text ?? ''
    )

    // Flag to determine if new content should be added after saving
    const [shouldTriggerNewContent, setShouldTriggerNewContent] = useState(
        false
    )

    // Track the original optionItemState when editor is activated
    // This allows reverting if user clicks away without making changes
    const originalStateRef = useRef<OptionItemState | undefined>(undefined)

    // Function to set the original state from outside (e.g., from ReplacementOption's auto-focus effect)
    const setOriginalState = useCallback((state: OptionItemState | undefined) => {
        originalStateRef.current = state
    }, [])
    // Create unique identifier for editor tracking - no memoization needed for simple string concatenation
    const formattedContentId = `replacement-option-${
        contentItem.id || 'default'
    }`
    const ckeToggle = useCKEditorToggle(formattedContentId)

    // Track activation time to prevent premature blur handling
    const activationTimestampRef = useRef<number>(0)

    // Guard against Firefox double-blur: Firefox fires handleBlur twice,
    // and the first call clears originalStateRef, causing the second to
    // use the wrong change detection path and trigger a spurious save.
    // [Firefox 120+, CKEditor 5.x as of 2026-02]
    // TODO: Re-evaluate if Firefox behavior changes in future CKEditor versions
    const blurHandledRef = useRef(false)

    // Guard against spurious blur CKEditor fires during initialization, before
    // onConfigComplete runs. This ref is set true by onConfigComplete and reset
    // on each activation.
    const editorMountedRef = useRef(false)

    const projectSettings = useProjectSettings()
    const unitOfMeasureSetting = projectSettings?.unitOfMeasure ?? UnitsOfMeasure.IMPERIAL


    // Get save operation state to prevent blur during saves
    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )

    // Get tree mapping state and actions
    const {
        isTreeMappingInProgress,
        deactivateAllEditors,
    } = useShallowSelector((s) => ({
        isTreeMappingInProgress: s.isTreeMappingInProgress,
        deactivateAllEditors: s.actions.deactivateAllEditors,
    }))

    const initialSuggestions = useMemo(
        () => extractSuggestions([contentItem]),
        [contentItem.id, contentItem.changeType]
    )

    const { editorFocusRef, focusEditor } = useContentEditorFocus({
        contentItemId: contentItem.id,
        activate: ckeToggle.activate,
        controlType: 'replacementOption',
    })

    /**
     * Handles the focus capture event for the replacement option.
     * Activates the CKEditor when the element receives focus.
     * Records activation time to prevent race conditions with rapid clicks.
     * Deactivates all other editors first to ensure only one is active at a time.
     */
    const handleOnFocusCapture = useCallback((): void => {
        // Reset blur guard on focus so the next blur can be processed
        blurHandledRef.current = false
        // Reset mount guard - will be set true by onConfigComplete
        editorMountedRef.current = false

        // Store the original optionItemState before editing starts
        // This allows reverting if no changes are made
        // Only set if not already set (e.g., by auto-focus from OptedIn click)
        if (!originalStateRef.current) {
            // Read from store at execution time to avoid unnecessary re-renders
            const currentElementToAdd = useWorkspace2Store.getState().textContentElementToAdd
            if (currentElementToAdd?.id === contentItem.id && currentElementToAdd?.optionItemState) {
                originalStateRef.current = currentElementToAdd.optionItemState
            } else if (contentItem.optionItemState) {
                originalStateRef.current = contentItem.optionItemState
            }
        }

        // Deactivate all other editors first
        deactivateAllEditors()

        // Record activation time for race condition prevention
        activationTimestampRef.current = Date.now()
        // Activate editor immediately for faster response
        focusEditor()
    }, [focusEditor, deactivateAllEditors, contentItem.optionItemState, contentItem.id])

    /**
     * Handles the blur event when CKEditor loses focus.
     * Processes content changes, updates the content item, and deactivates the editor.
     *
     * If no changes were made AND the element was previously OptedIn:
     * - Reverts the state back to OptedIn optimistically (no API call)
     * If changes were made:
     * - Saves the changes via API call through onValueChange
     */
    const handleBlur = useCallback(
        (_, editor): void => {
            try {
                // Ignore spurious blur fired during CKEditor initialization (before
                // onConfigComplete). Without this guard, blurHandledRef gets set to
                // true and blocks the real user blur later.
                if (!editorMountedRef.current) {
                    return
                }

                // Guard against Firefox double-blur firing
                if (blurHandledRef.current) {
                    return
                }

                // Block blur during critical operations or if activation is too recent
                const isBlurBlocked =
                    isSaveOperationInProgress ||
                    (isTreeMappingInProgress ?? false) ||
                    Date.now() - activationTimestampRef.current < 100

                if (isBlurBlocked) {
                    return
                }

                // Mark blur as handled AFTER the block check, so blocked blurs
                // don't prevent a legitimate subsequent blur from processing
                blurHandledRef.current = true

                // Read directly from the CKEditor instance: blur fires BEFORE
                // pending change:data events, so unsavedData is still stale.
                if (!editor?.getData) {
                    blurHandledRef.current = false
                    return
                }
                const editorData = editor.getData()
                const currentContent = getUnresolvedChanges(
                    editorData,
                    editorBaselineText,
                    initialSuggestions.length > 0
                )

                const isUOM = hasUOMContent(contentItem)
                const hasNoChanges = isUOM 
                    ? hasUOMContentMatchesSetting(
                        currentContent,
                        contentItem,
                        unitOfMeasureSetting
                    )
                    : hasContentNotChanged({
                        currentContent,
                        contentItem,
                        isOptedInEdit: !!originalStateRef.current,
                        allowEmpty,
                        editorData,
                    })

                if (hasNoChanges) {
                    originalStateRef.current = undefined
                    editorMountedRef.current = false
                    return ckeToggle.deactivate()
                }

                originalStateRef.current = undefined

                // Handle empty content by falling back to placeholder text if available
                const isContentEmpty = currentContent.trim() === ''
                const hasPlaceholderText =
                    contentItem.placeholderText != null &&
                    contentItem.placeholderText !== ''

                const itemForUpdate = isUOM ? { ...contentItem, text: currentContent } : contentItem
                const updatedElements = getUpdatedContentItem(itemForUpdate)

                let finalText: string
                if (isUOM) {
                    const uomEl = updatedElements.find((el) => el.type === 'UnitsOfMeasure')
                    const imperial = uomEl?.imperialUnits?.trim() ?? ''
                    const metric = uomEl?.metricUnits?.trim() ?? ''
                    finalText = metric ? `${imperial} (${metric})` : imperial
                } else {
                    finalText = isContentEmpty && hasPlaceholderText
                        ? contentItem.placeholderText!
                        : currentContent
                }

                // Create updated content item with processed text and elements
                // Any edit to a ReplacementOption should result in OptedIn state
                const updatedContentItem: Partial<TextContentElement> = {
                    ...contentItem,
                    text: removeUnitOfMeasureDelimiters(finalText),
                    placeholderText: removeUnitOfMeasureDelimiters(
                        contentItem.placeholderText
                    ),
                    elements: updatedElements,
                    // Always set OptedIn when content is edited
                    optionItemState: PROPERTY_EDITOR_PILL_STATE_VARIANT.OptedIn,
                }

                // Notify parent component of the content change
                onValueChange?.(updatedContentItem, false)

                // Deactivate the editor
                editorMountedRef.current = false
                ckeToggle.deactivate()
            } catch (error) {
                // Ensure editor is deactivated even on error to prevent stuck state
                editorMountedRef.current = false
                try {
                    ckeToggle.deactivate()
                } catch (deactivateError) {
                    // Silently handle deactivation errors
                }

                // Reset blur guard to allow retry
                blurHandledRef.current = false
            }
        },
        [
            contentItem,
            onValueChange,
            ckeToggle,
            editorBaselineText,
            initialSuggestions,
            removeUnitOfMeasureDelimiters,
            getUpdatedContentItem,
            isSaveOperationInProgress,
            isTreeMappingInProgress,
            unitOfMeasureSetting,
            allowEmpty,     
        ]
    )
    const {
        handleDeactivateAndAddContentReplacementOption,
        executeSaveAndAddContent,
        executeSaveOnly,
    } = useKeyOperation({
        editorRef,
        contentItemId: contentItem.id,
        handleOnBlur: handleBlur,
        shouldTriggerNewContent,
        setShouldTriggerNewContent,
        unFocusEditor: ckeToggle.deactivate,
    })

    const handleOnChange = useCallback(
        (_, editor): void => {
            try {
                const hasInitialSuggestions = initialSuggestions.length > 0

                const editorData = editor.getData()

                // Compare against editorBaselineText (the formatted/trimmed text actually
                // loaded into CKEditor) instead of contentItem.text. This prevents false
                // positive change detection in Firefox, where CKEditor fires onChange during
                // initialization - the editor text naturally matches what was loaded, not
                // the raw contentItem.text (which may contain UOM delimiters, extra whitespace, etc.)
                // [Firefox 120+, CKEditor 5.x as of 2026-02]
                const textChanges = getUnresolvedChanges(
                    editorData,
                    editorBaselineText,
                    hasInitialSuggestions
                )

                setUnsavedData(textChanges)
            } catch (error) {
                // Don't update unsavedData on error - keep previous state
            }
        },
        [initialSuggestions, editorBaselineText, contentItem.id]
    )

    /**
     * Handles keyboard events for the replacement option.
     * Manages save operations, content addition, and editor deactivation based on key combinations.
     */
    const handleOnKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLElement>): void => {
            // Handle save-only operations (e.g., Ctrl+S)
            if (executeSaveOnly(event)) {
                return
            }

            const hasNoChanges = hasContentNotChanged({
                currentContent: unsavedData,
                contentItem,
                isOptedInEdit: false,
            })

            // Set flag to indicate new content should be triggered
            setShouldTriggerNewContent(true)

            // Case 1: Handle immediate deactivation and content addition (e.g., Escape key)
            if (
                handleDeactivateAndAddContentReplacementOption(
                    event,
                    hasNoChanges
                )
            ) {
                return
            }

            // Case 2: Handle save and add new content (e.g., Enter key)
            if (executeSaveAndAddContent(event)) {
                return
            }

            // Reset the trigger flag if no special operation was performed
            setShouldTriggerNewContent(false)
        },
        [
            executeSaveOnly,
            unsavedData,
            contentItem.text,
            handleDeactivateAndAddContentReplacementOption,
            executeSaveAndAddContent,
        ]
    )

    // Create onReady handler that sets up editor references
    const onReady = useCallback(
        (editor): void => {
            editorFocusRef.current = editor
            registerEditorRef(editor)
        },
        [editorFocusRef, registerEditorRef]
    )

    // Wrap markMountComplete to also set editorMountedRef so handleBlur
    // knows the editor is ready to accept blur events.
    // markMountComplete runs first — if it throws, the ref stays false and
    // handleBlur will continue to suppress blurs (safe default).
    const handleConfigComplete = useCallback(() => {
        ckeToggle.markMountComplete()
        editorMountedRef.current = true
    }, [ckeToggle])

    // Return individual handler functions - simpler than memoized object
    return {
        handlers: {
            onReady,
            onChange: handleOnChange,
            onBlur: handleBlur,
            onFocusCapture: handleOnFocusCapture,
            onKeyDown: handleOnKeyDown,
            onConfigComplete: handleConfigComplete,
        },
        ckeToggle, // Return the toggle so ReplacementOption can use it
        setOriginalState, // Allows setting original state for revert on blur without changes
    }
}
